import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import type { AbstractLeadFormState } from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import { BasicSuccessPage } from "@/component/assets/AbstractLeadForm/Steps/BasicSuccessPage";
import type {
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	AbstractLeadFormFields,
	LeadRequest,
	ProductType,
} from "@/component/assets/AbstractLeadForm/types";
import type { CalculatorError } from "@/component/assets/CalculatorError";
import type { EventComponentType } from "@/shared/lib/google-analytics-4.type";
import { logError } from "@/shared/lib/sentry-error";
import type { ProgressButtonState } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import { calculate, uniqueByBankCode } from "../Calculator/domain";
import * as Step from "./Steps";
import type {
	HomeSavingsCalculatorRequest,
	HomeSavingsLeadCalcRequest,
	HomeSavingsLeadCalcResponse,
	HomeSavingsResponseItem,
} from "./types";
import "./style.scss";

interface FormFields extends AbstractLeadFormFields {
	monthlyDeposit: number;
	focusOn: boolean;
	question?: string;
}

interface State extends AbstractLeadFormState<FormFields> {
	search: Record<string, any>;
	errorMessages: string[];
	submitState: ProgressButtonState;
	homeSavingsResults: HomeSavingsResponseItem[];
	selectedResponseItem: HomeSavingsResponseItem;
}

/**
 * @Shortcode
 *
 * @title "Lakástakarék kalkulátor"
 * @shortcode "home_savings_small"
 * @slug "lakastakarek"
 * @type "calculator"
 */
export class HomeSavingsSmallCalculator extends AbstractLeadForm<
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	FormFields,
	{},
	State
> {
	// EventParams
	protected readonly productTypes: string = "BUILDING_SOCIETY";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "",
		outsideTitle: "Lakástakarék kalkulátor",
		outsideTitleLink: "/megtakaritasok/lakastakarek/",
		bottomLogo: true,
		colorScheme: "dark",
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			search: undefined,
			errorMessages: undefined,
			submitState: undefined,
			homeSavingsResults: [],
			selectedResponseItem: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const { submitState, homeSavingsResults, values, errorMessages } = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" submitState={submitState} />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					results={homeSavingsResults}
					selectResult={this.selectResponse}
					submitState={submitState}
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success"
					title={this.renderLeadFormTitle()}
					submitState={submitState}
					canStepBack
				/>
				<BasicSuccessPage
					stepName="success"
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
					userEmail={values.email}
				/>
				<Step.ErrorStep stepName="error-page" errors={errorMessages} canStepBack />
			</>
		);
	}

	protected renderLeadFormTitle(): ReactNode {
		const { selectedResponseItem } = this.state;

		return (
			<div className="text-center card-title">
				Most{" "}
				<mark className="text-success bg-white">
					{formatText(selectedResponseItem?.interestBonusRate, "0%")}
				</mark>{" "}
				kamatbónusszal
				<br />
				<mark className="text-success bg-white">
					{formatText(selectedResponseItem?.fullSavingsNoLoan, "ft")}
				</mark>{" "}
				gyűjthető össze lakás célra.
			</div>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success":
				this.sendLead()
					.then((_response) => {
						super.onStep("success", false);
					})
					.catch((error: Error) => {
						// TODO: Hibakezelés
						logError(error);
					});
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	@autobind
	protected selectResponse(response: HomeSavingsResponseItem) {
		this.setState({
			selectedResponseItem: response,
		});
	}

	@autobind
	private onResult(target: string): void {
		const { values } = this.state;
		this.setState({ submitState: "loading" });
		const searchParams = {
			monthlyDeposit: values.monthlyDeposit,
			focusOn: values.focusOn,
			marketingNeeded: false,
			comparator: "AER",
		} as unknown as HomeSavingsCalculatorRequest;

		const errors = [];

		this.promisePool
			.add(calculate(searchParams))
			.then((results) => {
				if (results?.normalList?.length) {
					this.setState({
						submitState: undefined,
						search: searchParams,
						homeSavingsResults: uniqueByBankCode(results.normalList),
						selectedResponseItem: results.normalList[0],
					});
				} else if (!errors.length) {
					errors.push("Nincs számodra megfelelő a bankok kínálatai között.");
				}
			})
			.catch((error: CalculatorError) => {
				if (error?.fieldErrors) {
					Object.values(error.fieldErrors).forEach((errorMsg: string) => {
						errors.push(errorMsg);
					});
				} else if (error?.message) {
					errors.push(`${error.message.split(".")[0]}.`);
				} else {
					errors.push("Ismeretlen hiba történt a kalkuláció közben!");

					logError(error);
				}
			})
			.finally(() => {
				this.setState(
					{
						submitState: undefined,
						errorMessages: errors,
					},
					() => {
						super.onStep(errors.length ? "error-page" : target, true);
					},
				);

				this.sendCalculationEvent(errors);
			});
	}

	protected getClassName(): string {
		return `${super.getClassName()} home-savings react-small-calculator`;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-building-society";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const { selectedResponseItem, values } = this.state;

		return {
			lastName: values.lastName,
			firstName: values.firstName,
			productName: selectedResponseItem.name,
		};
	}

	protected getProductType(): ProductType {
		return "BUILDING_SOCIETY";
	}

	protected getLeadData(): LeadRequest<HomeSavingsLeadCalcRequest, HomeSavingsLeadCalcResponse> {
		const { values, selectedResponseItem } = this.state;
		const data = super.getLeadData() as LeadRequest<HomeSavingsLeadCalcRequest, HomeSavingsLeadCalcResponse>;
		data.request = {
			...data.request,
			monthlyDeposit: values.monthlyDeposit,
			focusOn: values.focusOn,
			marketingNeeded: false,
			comparator: "AER",
			question: values.question,
		};
		data.response = {
			...selectedResponseItem,
			productName: selectedResponseItem.name,
		};

		return data;
	}

	protected getComponentName(): string {
		return "HomeSavingsSmallCalculator";
	}
}

export default HomeSavingsSmallCalculator;
